import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Container, Typography, Paper, Alert } from "@mui/material";
import { useCashOutV2 } from "@/hooks/useCashOutV2";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { CashOutFormV2 } from "@/components/cashOut/CashOutFormV2";
import { ErrorDisplay } from "@/components/common/ErrorDisplay";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { UserSelector } from "@/components/cashOut/UserSelector";

export function CashOutEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { cashOutPermissions, isManagerOrAbove } = usePermissions();
  const { getCashOut, updateCashOut, isUpdating } = useCashOutV2();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // @ts-ignore - tRPC type issues
  const { data: cashOut, error, isLoading } = getCashOut({ id: id! });

  // Set the initial selected user when cashout data loads
  useEffect(() => {
    if (cashOut?.user?.id) {
      setSelectedUserId(cashOut.user.id);
    }
  }, [cashOut]);

  const handleSubmit = async (data: any) => {
    try {
      const updateData: any = {
        id: id!,
        ...data,
      };
      
      // Add selected user if manager/admin changed the user
      if (isManagerOrAbove && selectedUserId && selectedUserId !== cashOut?.user?.id) {
        updateData.onBehalfOfUserId = selectedUserId;
      }
      
      await updateCashOut(updateData);
      navigate("/cash-outs");
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleCancel = () => {
    navigate("/cash-outs");
  };

  if (!user) {
    return null;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <ErrorDisplay error={error} />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <LoadingSpinner />
      </Container>
    );
  }

  if (!cashOut) {
    return (
      <Container maxWidth="lg">
        <ErrorDisplay error={new Error("Cash out not found")} />
      </Container>
    );
  }

  // Check permissions - users can edit their own unapproved cashouts, managers can edit any
  const isOwnCashOut = cashOut.user.id === user.id;
  const canEdit =
    (isOwnCashOut && cashOutPermissions.canEditOwn) ||
    cashOutPermissions.canEditAll;

  if (!canEdit) {
    return (
      <Container maxWidth="lg">
        <ErrorDisplay
          error={new Error("You do not have permission to edit this cash out")}
        />
      </Container>
    );
  }

  // Check if cash out is already approved
  if (cashOut.approved) {
    return (
      <Container maxWidth="lg">
        <ErrorDisplay error={new Error("Cannot edit an approved cash out")} />
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Edit Cash Out
        </Typography>

        {/* User selector for ADMIN/MANAGER only */}
        {isManagerOrAbove && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <UserSelector
              value={selectedUserId}
              onChange={setSelectedUserId}
              label="Reassign Cash Out To User"
              helperText="You can reassign this cash out to a different user."
            />
            {selectedUserId && selectedUserId !== cashOut.user?.id && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                You are reassigning this cash out from {cashOut.user?.firstName} {cashOut.user?.lastName} to another user.
              </Alert>
            )}
          </Paper>
        )}

        <CashOutFormV2
          initialData={cashOut}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isUpdating}
          venue={cashOut.venue}
        />
      </Box>
    </Container>
  );
}
